/**
 * 系统设置 · AI 配置 API（AICFG-1）· 真契约源 apps/api-server/src/api_server/routes/ai_settings.py
 *
 * 🔴 三块（连接 / 功能分配 / 调用记录）各自独立可用（API-First）——本文件只是给
 * admin-web 用的薄封装。整个 `/api/v1/ai/*` 仅 owner / tech_admin，其它角色 403。
 */
import { api } from "./api";
import type {
  AiCallRow, AiConnection, AiConnectionCreate, AiConnectionPatch, AiConnectionTestBody,
  AiConnectionTestResult, AiFeature, AiFeaturePatch, AiUsageRow,
} from "./types/aiSettings";

const BASE = "/api/v1/ai";

export function listAiConnections(): Promise<AiConnection[]> {
  return api<AiConnection[]>(`${BASE}/connections`);
}

export function createAiConnection(body: AiConnectionCreate): Promise<AiConnection> {
  return api<AiConnection>(`${BASE}/connections`, { method: "POST", body: JSON.stringify(body) });
}

/** 就地改字段/停用/启用——只传要改的那几个 key。 */
export function patchAiConnection(id: string, body: AiConnectionPatch): Promise<AiConnection> {
  return api<AiConnection>(`${BASE}/connections/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

/** 测已保存的连接——结果写回 last_test，同时记一条调用记录。 */
export function testSavedAiConnection(id: string): Promise<AiConnectionTestResult> {
  return api<AiConnectionTestResult>(`${BASE}/connections/${id}/test`, { method: "POST" });
}

/** 弹窗「先测试」——保存前试连，key 不落库、不记日志。 */
export function testUnsavedAiConnection(body: AiConnectionTestBody): Promise<AiConnectionTestResult> {
  return api<AiConnectionTestResult>(`${BASE}/connections:test`, { method: "POST", body: JSON.stringify(body) });
}

/** 模型候选（`<datalist>` 用）——失败回 []，前端退回手填。 */
export function getAiConnectionModels(id: string): Promise<string[]> {
  return api<string[]>(`${BASE}/connections/${id}/models`);
}

export function listAiFeatures(): Promise<AiFeature[]> {
  return api<AiFeature[]>(`${BASE}/features`);
}

export function patchAiFeature(feature: string, body: AiFeaturePatch): Promise<AiFeature> {
  return api<AiFeature>(`${BASE}/features/${feature}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function getAiUsage(month: string): Promise<AiUsageRow[]> {
  return api<AiUsageRow[]>(`${BASE}/usage?month=${encodeURIComponent(month)}`);
}

export function listAiCalls(params: { feature?: string; result?: "ok" | "fail"; limit?: number } = {}): Promise<AiCallRow[]> {
  const qs = new URLSearchParams();
  if (params.feature) qs.set("feature", params.feature);
  if (params.result) qs.set("result", params.result);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return api<AiCallRow[]>(`${BASE}/calls${suffix}`);
}
